import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  Music2,
  DollarSign,
  TrendingUp,
  Package,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Upload
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isArtist, loading: authLoading } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(null);
  const fileInputRef = useRef(null);

  // Auto-open the upload dialog when ?upload=1 is in the URL (Navbar quick link)
  useEffect(() => {
    if (searchParams.get('upload') === '1') {
      setIsUploadOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('upload');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [newTrack, setNewTrack] = useState({
    title: '',
    description: '',
    genre: 'Afrobeats',
    bpm: 120,
    price_sync: 99,
    price_commercial: 299,
    price_nft: 499,
    price_lyrics: 49,
    artwork_url: '',
    content_rating: 'General',
    voice: 'Instrumental',
    language: 'English',
    moods: [],
    character: []
  });
  const [vocab, setVocab] = useState({ content_rating: [], voice: [], language: [], moods: [], character: [] });
  const [audioFile, setAudioFile] = useState(null);
  const [lyricsFile, setLyricsFile] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/filters/vocab`)
      .then((r) => setVocab(r.data))
      .catch((e) => console.error('vocab load failed', e));
  }, []);

  useEffect(() => {
    // Wait for auth to load before checking permissions
    if (authLoading) return;
    
    if (!isArtist && user?.role !== 'admin') {
      navigate('/login');
      return;
    }

    const fetchDashboard = async () => {
      try {
        const [dashboardRes, tracksRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/artist/dashboard`),
          axios.get(`${BACKEND_URL}/api/tracks`)
        ]);
        setDashboard(dashboardRes.data);
        const artistTracks = tracksRes.data.filter(t => t.artist_id === user?._id || t.artist_id === user?.id);
        setTracks(artistTracks);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [isArtist, user, navigate, authLoading]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/tracks`, newTrack);
      const createdTrack = response.data;

      // If artist picked an audio file in the same dialog, upload it now
      if (audioFile && createdTrack?.id) {
        const formData = new FormData();
        formData.append('file', audioFile);
        try {
          await axios.post(
            `${BACKEND_URL}/api/tracks/${createdTrack.id}/upload-audio`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } catch (err) {
          console.error('Audio upload error during create:', err);
          toast.warning('Track created — audio upload failed. You can retry from the table.');
        }
      }
      // If artist picked a lyrics file, upload it now
      if (lyricsFile && createdTrack?.id) {
        const formData = new FormData();
        formData.append('file', lyricsFile);
        try {
          await axios.post(
            `${BACKEND_URL}/api/tracks/${createdTrack.id}/upload-lyrics`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } catch (err) {
          console.error('Lyrics upload error during create:', err);
          toast.warning('Track created — lyrics upload failed. You can retry later.');
        }
      }
      toast.success('Track created');

      setIsUploadOpen(false);
      setAudioFile(null);
      setLyricsFile(null);
      setNewTrack({
        title: '',
        description: '',
        genre: 'Afrobeats',
        bpm: 120,
        price_sync: 99,
        price_commercial: 299,
        price_nft: 499,
        price_lyrics: 49,
        artwork_url: '',
        content_rating: 'General',
        voice: 'Instrumental',
        language: 'English',
        moods: [],
        character: []
      });
      // Refresh tracks
      const tracksRes = await axios.get(`${BACKEND_URL}/api/tracks`);
      const artistTracks = tracksRes.data.filter(t => t.artist_id === user?._id || t.artist_id === user?.id);
      setTracks(artistTracks);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create track');
    } finally {
      setUploading(false);
    }
  };

  const toggleTag = (key, value) => {
    setNewTrack((prev) => {
      const current = prev[key] || [];
      return {
        ...prev,
        [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
      };
    });
  };

  const handleAudioUpload = async (trackId, file) => {
    setUploadingAudio(trackId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${BACKEND_URL}/api/tracks/${trackId}/upload-audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Audio file uploaded!');
      // Refresh tracks
      const tracksRes = await axios.get(`${BACKEND_URL}/api/tracks`);
      const artistTracks = tracksRes.data.filter(t => t.artist_id === user?._id || t.artist_id === user?.id);
      setTracks(artistTracks);
    } catch (error) {
      console.error('Audio upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload audio');
    } finally {
      setUploadingAudio(null);
    }
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm('Are you sure you want to delete this track?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/tracks/${trackId}`);
      toast.success('Track deleted');
      setTracks(tracks.filter(t => t.id !== trackId));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete track');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const stats = [
    { label: 'Total Tracks', value: dashboard?.total_tracks || 0, icon: Music2, color: 'text-emerald-400' },
    { label: 'Total Sales', value: dashboard?.total_sales || 0, icon: Package, color: 'text-cyan-400' },
    { label: 'Total Earnings', value: formatCurrency(dashboard?.total_earnings || 0), icon: DollarSign, color: 'text-emerald-400' },
    { label: 'Avg. per Track', value: formatCurrency(dashboard?.total_tracks > 0 ? (dashboard?.total_earnings / dashboard?.total_tracks) : 0), icon: TrendingUp, color: 'text-cyan-400' }
  ];

  const genres = ['Afrobeats', 'Hip Hop', 'R&B', 'Amapiano', 'Highlife', 'Ambient', 'Electronic', 'Jazz', 'Soul', 'Afro-Soul', 'Afro-Fusion'];

  return (
    <div className="min-h-screen bg-gradient-dark pt-24 pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-2 block">
              Artist Dashboard
            </span>
            <h1 className="text-3xl font-medium text-white">
              Welcome back, {user?.artist_name || user?.name}
            </h1>
          </div>

          <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold rounded-lg" data-testid="upload-track-btn">
                <Plus className="w-4 h-4 mr-2" />
                Upload Track
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">Upload New Track</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Title</Label>
                  <Input
                    value={newTrack.title}
                    onChange={(e) => setNewTrack({ ...newTrack, title: e.target.value })}
                    placeholder="Track title"
                    required
                    className="bg-slate-800 border-white/10 text-white rounded-lg"
                    data-testid="track-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Description</Label>
                  <Textarea
                    value={newTrack.description}
                    onChange={(e) => setNewTrack({ ...newTrack, description: e.target.value })}
                    placeholder="Describe your track"
                    className="bg-slate-800 border-white/10 text-white rounded-lg"
                    data-testid="track-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Genre</Label>
                    <Select value={newTrack.genre} onValueChange={(v) => setNewTrack({ ...newTrack, genre: v })}>
                      <SelectTrigger className="bg-slate-800 border-white/10 text-white rounded-lg" data-testid="track-genre-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/10">
                        {genres.map((genre) => (
                          <SelectItem key={genre} value={genre} className="text-white hover:bg-white/5">
                            {genre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300">BPM</Label>
                    <Input
                      type="number"
                      value={newTrack.bpm}
                      onChange={(e) => setNewTrack({ ...newTrack, bpm: parseInt(e.target.value) || 0 })}
                      className="bg-slate-800 border-white/10 text-white rounded-lg"
                      data-testid="track-bpm-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Artwork URL (optional)</Label>
                  <Input
                    value={newTrack.artwork_url}
                    onChange={(e) => setNewTrack({ ...newTrack, artwork_url: e.target.value })}
                    placeholder="https://..."
                    className="bg-slate-800 border-white/10 text-white rounded-lg"
                    data-testid="track-artwork-input"
                  />
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-xs">Sync ($)</Label>
                    <Input
                      type="number"
                      value={newTrack.price_sync}
                      onChange={(e) => setNewTrack({ ...newTrack, price_sync: parseFloat(e.target.value) || 0 })}
                      className="bg-slate-800 border-white/10 text-white rounded-lg"
                      data-testid="track-price-sync"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-xs">Commercial ($)</Label>
                    <Input
                      type="number"
                      value={newTrack.price_commercial}
                      onChange={(e) => setNewTrack({ ...newTrack, price_commercial: parseFloat(e.target.value) || 0 })}
                      className="bg-slate-800 border-white/10 text-white rounded-lg"
                      data-testid="track-price-commercial"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-xs">NFT ($)</Label>
                    <Input
                      type="number"
                      value={newTrack.price_nft}
                      onChange={(e) => setNewTrack({ ...newTrack, price_nft: parseFloat(e.target.value) || 0 })}
                      className="bg-slate-800 border-white/10 text-white rounded-lg"
                      data-testid="track-price-nft"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-xs">Lyrics ($)</Label>
                    <Input
                      type="number"
                      value={newTrack.price_lyrics}
                      onChange={(e) => setNewTrack({ ...newTrack, price_lyrics: parseFloat(e.target.value) || 0 })}
                      className="bg-slate-800 border-white/10 text-white rounded-lg"
                      data-testid="track-price-lyrics"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Content Rating</Label>
                    <Select value={newTrack.content_rating} onValueChange={(v) => setNewTrack({ ...newTrack, content_rating: v })}>
                      <SelectTrigger className="bg-slate-800 border-white/10 text-white rounded-lg" data-testid="track-content-rating">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/10">
                        {vocab.content_rating.map((opt) => (
                          <SelectItem key={opt} value={opt} className="text-white hover:bg-white/5">{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Voice</Label>
                    <Select value={newTrack.voice} onValueChange={(v) => setNewTrack({ ...newTrack, voice: v })}>
                      <SelectTrigger className="bg-slate-800 border-white/10 text-white rounded-lg" data-testid="track-voice">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/10">
                        {vocab.voice.map((opt) => (
                          <SelectItem key={opt} value={opt} className="text-white hover:bg-white/5">{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Language</Label>
                    <Select value={newTrack.language} onValueChange={(v) => setNewTrack({ ...newTrack, language: v })}>
                      <SelectTrigger className="bg-slate-800 border-white/10 text-white rounded-lg" data-testid="track-language">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/10">
                        {vocab.language.map((opt) => (
                          <SelectItem key={opt} value={opt} className="text-white hover:bg-white/5">{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Moods <span className="text-slate-500 text-xs">(click to toggle)</span></Label>
                  <div className="flex flex-wrap gap-2" data-testid="track-moods-group">
                    {vocab.moods.map((m) => {
                      const active = newTrack.moods.includes(m);
                      return (
                        <button
                          type="button"
                          key={m}
                          onClick={() => toggleTag('moods', m)}
                          data-testid={`track-mood-${m}`}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-emerald-500 border-emerald-500 text-slate-900 font-medium' : 'bg-slate-800 border-white/10 text-slate-300 hover:bg-slate-700'}`}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Character <span className="text-slate-500 text-xs">(click to toggle)</span></Label>
                  <div className="flex flex-wrap gap-2" data-testid="track-character-group">
                    {vocab.character.map((c) => {
                      const active = newTrack.character.includes(c);
                      return (
                        <button
                          type="button"
                          key={c}
                          onClick={() => toggleTag('character', c)}
                          data-testid={`track-character-${c}`}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${active ? 'bg-emerald-500 border-emerald-500 text-slate-900 font-medium' : 'bg-slate-800 border-white/10 text-slate-300 hover:bg-slate-700'}`}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Audio File <span className="text-slate-500 text-xs">(MP3 or WAV, max 50MB)</span></Label>
                  <Input
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/wave,audio/x-wav"
                    onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                    className="bg-slate-800 border-white/10 text-white rounded-lg file:bg-emerald-500 file:text-slate-900 file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:font-semibold"
                    data-testid="track-audio-file"
                  />
                  {audioFile && (
                    <p className="text-xs text-emerald-400" data-testid="track-audio-selected">
                      Selected: {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Lyrics File <span className="text-slate-500 text-xs">(PDF or TXT, max 5MB — optional)</span></Label>
                  <Input
                    type="file"
                    accept="application/pdf,text/plain,.txt,.pdf,.md"
                    onChange={(e) => setLyricsFile(e.target.files?.[0] || null)}
                    className="bg-slate-800 border-white/10 text-white rounded-lg file:bg-emerald-500 file:text-slate-900 file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:font-semibold"
                    data-testid="track-lyrics-file"
                  />
                  {lyricsFile && (
                    <p className="text-xs text-emerald-400" data-testid="track-lyrics-selected">
                      Selected: {lyricsFile.name} ({(lyricsFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold rounded-lg"
                  data-testid="submit-track-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Track'
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-gradient-card border border-white/5 p-6 rounded-xl"
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
              <p className="text-2xl font-semibold text-white">{stat.value}</p>
              <p className="text-sm text-slate-400">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="bg-gradient-card border border-white/5 p-6 rounded-xl mb-10">
          <h2 className="text-lg font-semibold text-white mb-6">Earnings Overview</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dashboard?.monthly_data || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="month" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="earnings"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={{ fill: '#22c55e', strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tracks Table */}
        <div className="bg-gradient-card border border-white/5 rounded-xl overflow-hidden">
          <div className="p-6 border-b border-white/5">
            <h2 className="text-lg font-semibold text-white">Your Tracks</h2>
          </div>
          
          {tracks.length === 0 ? (
            <div className="p-12 text-center">
              <Music2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No tracks uploaded yet</p>
              <Button
                onClick={() => setIsUploadOpen(true)}
                className="mt-4 bg-emerald-500 text-slate-900 hover:bg-emerald-400 rounded-lg"
              >
                Upload Your First Track
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="tracks-table">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Track</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Genre</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Audio</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Sales</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">Earnings</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((track) => (
                    <tr key={track.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={track.artwork_url}
                            alt={track.title}
                            className="w-12 h-12 object-cover rounded"
                          />
                          <div>
                            <p className="text-white font-medium">{track.title}</p>
                            <p className="text-sm text-slate-400">{track.bpm} BPM</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-white/5 text-sm text-slate-300 rounded">
                          {track.genre}
                        </span>
                      </td>
                      <td className="p-4">
                        {track.audio_url ? (
                          <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded-full">
                            Uploaded
                          </span>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="audio/*"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleAudioUpload(track.id, e.target.files[0]);
                                }
                              }}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-white/10 text-slate-300 hover:bg-white/5"
                              disabled={uploadingAudio === track.id}
                              asChild
                            >
                              <span>
                                {uploadingAudio === track.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 mr-1" />
                                    Upload
                                  </>
                                )}
                              </span>
                            </Button>
                          </label>
                        )}
                      </td>
                      <td className="p-4 text-white">{track.total_sales || 0}</td>
                      <td className="p-4 text-emerald-400 font-medium">
                        {formatCurrency(track.total_earnings || 0)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/tracks/${track.id}`)}
                            className="text-slate-400 hover:text-white"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(track.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
